import { ResourceType } from "@common/types/auditLog";
import { APIProcessName, SQSProcessName } from "../process-names";
import { HistoryField, HistoryGroupBy } from "./history-fields";
import type { HistorySourceTypeConfig } from "./history-source-config";
import { TRANSACTION_FIELDS } from "./transaction-sources";

const BILL_FIELDS = [
    HistoryField.STATUS,
    HistoryField.PAID_AT,
    HistoryField.DUE,
    HistoryField.TOTAL,
    HistoryField.SUBTOTAL,
    HistoryField.TAX,
    HistoryField.DISCOUNT,
    HistoryField.CURRENCY,
    HistoryField.INVOICE_NUMBER,
    HistoryField.COUNTERPARTY_ID,
    HistoryField.FINANCIAL_ACCOUNT_ID,
    HistoryField.DESCRIPTION,
    HistoryField.NOTE,
    HistoryField.SOURCE,
    HistoryField.LINES,
];

const INVOICE_FIELDS = [
    ...BILL_FIELDS,
    HistoryField.UNCOLLECTABLE_AT,
    HistoryField.IS_SUBSCRIPTION,
];

export const billSources: HistorySourceTypeConfig[] = [
    {
        sourceType: APIProcessName.BILL_CREATE_MANUAL,
        resourceTypeConfigs: [
            { resourceType: ResourceType.BILL, fields: BILL_FIELDS },
        ],
        groupBy: HistoryGroupBy.NONE,
        platformTwins: null,
        enabled: true,
    },
    {
        sourceType: APIProcessName.BILL_REPLACE_IMPORTER,
        resourceTypeConfigs: [
            { resourceType: ResourceType.BILL, fields: BILL_FIELDS },
        ],
        groupBy: HistoryGroupBy.NONE,
        platformTwins: null,
        enabled: true,
    },
    {
        sourceType: APIProcessName.BILL_PATCH_IMPORTER,
        resourceTypeConfigs: [
            { resourceType: ResourceType.BILL, fields: BILL_FIELDS },
        ],
        groupBy: HistoryGroupBy.FIELD,
        platformTwins: null,
        enabled: true,
    },
    {
        sourceType: APIProcessName.BILL_UPDATE,
        resourceTypeConfigs: [
            { resourceType: ResourceType.BILL, fields: BILL_FIELDS },
        ],
        groupBy: HistoryGroupBy.FIELD,
        platformTwins: null,
        enabled: true,
    },
    {
        sourceType: APIProcessName.BILL_DELETE,
        resourceTypeConfigs: [
            { resourceType: ResourceType.BILL, fields: BILL_FIELDS },
        ],
        groupBy: HistoryGroupBy.NONE,
        platformTwins: null,
        enabled: true,
    },
    {
        sourceType: APIProcessName.BILL_LINE_ITEM_UPDATE,
        resourceTypeConfigs: [
            {
                resourceType: ResourceType.BILL,
                fields: [HistoryField.LINES],
            },
        ],
        groupBy: HistoryGroupBy.FIELD,
        platformTwins: null,
        enabled: true,
    },
    {
        sourceType: APIProcessName.BILL_LINE_ITEM_BULK_UPDATE,
        resourceTypeConfigs: [
            {
                resourceType: ResourceType.BILL,
                fields: [HistoryField.LINES],
            },
        ],
        groupBy: HistoryGroupBy.FIELD,
        platformTwins: null,
        enabled: true,
    },
    {
        sourceType: APIProcessName.BILL_DOCUMENT_LINK,
        resourceTypeConfigs: [
            { resourceType: ResourceType.BILL, fields: BILL_FIELDS },
        ],
        groupBy: HistoryGroupBy.NONE,
        platformTwins: null,
        enabled: true,
    },
    {
        sourceType: APIProcessName.BILL_DOCUMENT_UNLINK,
        resourceTypeConfigs: [
            { resourceType: ResourceType.BILL, fields: BILL_FIELDS },
        ],
        groupBy: HistoryGroupBy.NONE,
        platformTwins: null,
        enabled: true,
    },
    {
        sourceType: SQSProcessName.RAMP_BILLS_SYNC,
        resourceTypeConfigs: [
            { resourceType: ResourceType.BILL, fields: BILL_FIELDS },
        ],
        groupBy: HistoryGroupBy.SOURCE_COUNT_DISTINCT,
        platformTwins: null,
        enabled: true,
    },
];

export const invoiceSources: HistorySourceTypeConfig[] = [
    {
        sourceType: APIProcessName.INVOICE_CREATE_MANUAL,
        resourceTypeConfigs: [
            { resourceType: ResourceType.INVOICE, fields: INVOICE_FIELDS },
        ],
        groupBy: HistoryGroupBy.NONE,
        platformTwins: null,
        enabled: true,
    },
    {
        sourceType: APIProcessName.INVOICE_REPLACE_IMPORTER,
        resourceTypeConfigs: [
            { resourceType: ResourceType.INVOICE, fields: INVOICE_FIELDS },
        ],
        groupBy: HistoryGroupBy.NONE,
        platformTwins: null,
        enabled: true,
    },
    {
        sourceType: APIProcessName.INVOICE_PATCH_IMPORTER,
        resourceTypeConfigs: [
            { resourceType: ResourceType.INVOICE, fields: INVOICE_FIELDS },
        ],
        groupBy: HistoryGroupBy.FIELD,
        platformTwins: null,
        enabled: true,
    },
    {
        sourceType: APIProcessName.INVOICE_UPDATE,
        resourceTypeConfigs: [
            { resourceType: ResourceType.INVOICE, fields: INVOICE_FIELDS },
        ],
        groupBy: HistoryGroupBy.FIELD,
        platformTwins: null,
        enabled: true,
    },
    {
        sourceType: APIProcessName.INVOICE_DELETE,
        resourceTypeConfigs: [
            { resourceType: ResourceType.INVOICE, fields: INVOICE_FIELDS },
        ],
        groupBy: HistoryGroupBy.NONE,
        platformTwins: null,
        enabled: true,
    },
    {
        sourceType: APIProcessName.INVOICE_LINE_ITEM_UPDATE,
        resourceTypeConfigs: [
            {
                resourceType: ResourceType.INVOICE,
                fields: [HistoryField.LINES],
            },
        ],
        groupBy: HistoryGroupBy.FIELD,
        platformTwins: null,
        enabled: true,
    },
    {
        sourceType: APIProcessName.INVOICE_LINE_ITEM_BULK_UPDATE,
        resourceTypeConfigs: [
            {
                resourceType: ResourceType.INVOICE,
                fields: [HistoryField.LINES],
            },
        ],
        groupBy: HistoryGroupBy.FIELD,
        platformTwins: null,
        enabled: true,
    },
    {
        sourceType: APIProcessName.INVOICE_DOCUMENT_LINK,
        resourceTypeConfigs: [
            { resourceType: ResourceType.INVOICE, fields: INVOICE_FIELDS },
        ],
        groupBy: HistoryGroupBy.NONE,
        platformTwins: null,
        enabled: true,
    },
    {
        sourceType: APIProcessName.INVOICE_DOCUMENT_UNLINK,
        resourceTypeConfigs: [
            { resourceType: ResourceType.INVOICE, fields: INVOICE_FIELDS },
        ],
        groupBy: HistoryGroupBy.NONE,
        platformTwins: null,
        enabled: true,
    },
    {
        sourceType: SQSProcessName.STRIPE_INVOICES_SYNC,
        resourceTypeConfigs: [
            { resourceType: ResourceType.INVOICE, fields: INVOICE_FIELDS },
        ],
        groupBy: HistoryGroupBy.SOURCE_COUNT_DISTINCT,
        platformTwins: null,
        enabled: true,
    },
    {
        // Framework Stripe sync imports balance transactions and invoices in
        // one job; declared here because INVOICE_FIELDS is defined after the
        // transaction sources.
        sourceType: SQSProcessName.STRIPE_SYNC,
        resourceTypeConfigs: [
            {
                resourceType: ResourceType.TRANSACTION,
                fields: TRANSACTION_FIELDS,
            },
            { resourceType: ResourceType.INVOICE, fields: INVOICE_FIELDS },
        ],
        groupBy: HistoryGroupBy.FIELD,
        platformTwins: null,
        enabled: true,
    },
    {
        // Reference-only event (no diff fields) logged the moment a native
        // invoice email is delivered — see InvoiceAuditLogInternalService.logEmailSent.
        sourceType: SQSProcessName.INVOICE_NATIVE_EMAIL_SEND,
        resourceTypeConfigs: [
            { resourceType: ResourceType.INVOICE, fields: [] },
        ],
        groupBy: HistoryGroupBy.NONE,
        platformTwins: null,
        enabled: true,
    },
];
